// ServiceRequestSection — renders inside IncidentPanel between
// Linked Modules and Comments. Lists service requests with inline status
// dropdown, expandable description+history, and an "＋ Add" button.
import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { useServiceRequests, usePatchServiceRequest } from "../api/sitroom";
import {
  SR_TYPE_BY_ID,
  SR_STATUS_STYLE,
  SR_STATUSES,
  SR_PRIORITY_STYLE,
  formatDueDate,
} from "../data/serviceRequestTypes";
import { ServiceRequestModal } from "./ServiceRequestModal";
import { RoleGuard, hasMinRole } from "./RoleGuard";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";

const Pill = ({ children, color, bg, border, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: bg,
      border: `1px solid ${border}`,
      color,
      fontFamily: "DM Mono, monospace",
      fontSize: 9,
      padding: "2px 7px",
      borderRadius: 3,
      letterSpacing: "1px",
      textTransform: "uppercase",
      cursor: onClick ? "pointer" : "default",
    }}
    data-testid={testid}
  >
    {children}
  </button>
);

const StatusDropdown = ({ sr, canEdit }) => {
  const [open, setOpen] = useState(false);
  const patch = usePatchServiceRequest();
  const addToast = useStore((s) => s.addToast);
  const stStyle = SR_STATUS_STYLE[sr.status] || SR_STATUS_STYLE.requested;

  const change = async (next) => {
    if (next === sr.status) {
      setOpen(false);
      return;
    }
    try {
      await patch.mutateAsync({ sr_id: sr.id, body: { status: next } });
      addToast({ type: "info", title: "Status updated", body: `${sr.id} → ${SR_STATUS_STYLE[next].label}` });
      setOpen(false);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Update failed";
      addToast({ type: "s1", title: "Update failed", body: String(detail) });
    }
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <Pill
        bg={stStyle.bg}
        border={stStyle.border}
        color={stStyle.color}
        onClick={canEdit ? () => setOpen((v) => !v) : undefined}
        testid={`sr-status-${sr.id}`}
      >
        {stStyle.label}
      </Pill>
      {open && (
        <div
          className="absolute right-0 mt-1 rounded-md overflow-hidden z-20"
          style={{
            width: 160,
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.5)",
          }}
          data-testid={`sr-status-menu-${sr.id}`}
        >
          {SR_STATUSES.map((s) => {
            const sty = SR_STATUS_STYLE[s];
            return (
              <button
                key={s}
                onClick={() => change(s)}
                className="w-full text-left px-3 py-1.5 hover:bg-white/[0.04]"
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  color: sty.color,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
                data-testid={`sr-status-option-${sr.id}-${s}`}
              >
                {sty.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ServiceRequestCard = ({ sr, canEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = SR_TYPE_BY_ID[sr.type] || SR_TYPE_BY_ID.other;
  const Icon = meta.Icon;
  const stStyle = SR_STATUS_STYLE[sr.status] || SR_STATUS_STYLE.requested;
  const prio = SR_PRIORITY_STYLE[sr.priority] || SR_PRIORITY_STYLE.medium;
  const cancelled = sr.status === "cancelled";

  return (
    <div
      className="rounded-md mb-2"
      style={{
        background: "#0d0f18",
        border: `1px solid ${stStyle.border}`,
        opacity: cancelled ? 0.6 : 1,
      }}
      data-testid={`sr-card-${sr.id}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        className="w-full flex items-center gap-3 p-3 text-left cursor-pointer"
      >
        <span
          className="inline-flex items-center justify-center rounded-md flex-shrink-0"
          style={{
            width: 32, height: 32,
            background: `${meta.color}1a`, border: `1px solid ${meta.color}55`, color: meta.color,
          }}
        >
          <Icon size={14} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                color: "#9198b0",
                letterSpacing: "0.5px",
              }}
            >
              {sr.id}
            </span>
            <span
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                color: meta.color,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
              }}
            >
              {meta.labelEn}
            </span>
            <Pill color={prio.color} bg={`${prio.color}1a`} border={`${prio.color}55`}>
              {prio.label}
            </Pill>
          </div>
          <div
            className="mt-1 truncate"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 12.5,
              fontWeight: 700,
              color: cancelled ? "#9aa1b3" : "#f0f2f8",
              textDecoration: cancelled ? "line-through" : "none",
            }}
            data-testid={`sr-title-${sr.id}`}
          >
            {sr.title}
          </div>
          <div
            className="mt-1 flex items-center gap-3 flex-wrap"
            style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9aa1b3" }}
          >
            <span>{sr.assignee_name || "Unassigned"}</span>
            {sr.due_date && <span>due {formatDueDate(sr.due_date)}</span>}
          </div>
        </div>
        <StatusDropdown sr={sr} canEdit={canEdit} />
        {expanded ? <ChevronDown size={14} color="#9198b0" /> : <ChevronRight size={14} color="#9198b0" />}
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          {sr.description && (
            <div
              className="mb-3"
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                color: "#cfd1d8",
                lineHeight: 1.55,
              }}
              data-testid={`sr-description-${sr.id}`}
            >
              {sr.description}
            </div>
          )}
          <div
            className="mb-1"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 8,
              letterSpacing: "1.5px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            History
          </div>
          <div className="space-y-1" data-testid={`sr-history-${sr.id}`}>
            {(sr.history || []).map((h, i) => (
              <div
                key={i}
                style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9aa1b3" }}
              >
                {new Date(h.at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                {" · "}
                {h.action === "status_change"
                  ? `${h.from} → ${h.to}`
                  : h.action}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ServiceRequestSection = ({ incident }) => {
  const { user } = useAuth();
  const { data: srs = [] } = useServiceRequests(incident?.id, Boolean(incident?.id));
  const [showAdd, setShowAdd] = useState(false);
  const [presetType, setPresetType] = useState(null);
  const canEdit = hasMinRole(user?.role, "responder");

  // Open modal pre-selected when the AI Next Steps card triggers a SR suggestion
  const srPreset = useStore((s) => s.srPreset);
  const clearSrPreset = useStore((s) => s.clearSrPreset);
  React.useEffect(() => {
    if (srPreset.incidentId && srPreset.incidentId === incident?.id) {
      setPresetType(srPreset.type || null);
      setShowAdd(true);
      clearSrPreset();
    }
  }, [srPreset, incident?.id, clearSrPreset]);

  return (
    <div data-testid="sr-section">
      <div
        className="flex items-center justify-between pt-4 pb-1 mb-2"
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        <span>
          Service Requests
          {srs.length > 0 && (
            <span
              className="ml-2"
              style={{
                background: "rgba(24,95,165,0.18)",
                border: "1px solid rgba(24,95,165,0.55)",
                color: "#9ec3ec",
                padding: "1px 6px",
                borderRadius: 3,
                fontSize: 9,
              }}
              data-testid="sr-section-count"
            >
              {srs.length}
            </span>
          )}
        </span>
        <RoleGuard minRole="responder">
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-md"
            style={{
              background: "rgba(24,95,165,0.10)",
              border: "1px solid rgba(24,95,165,0.45)",
              color: "#9ec3ec",
              fontFamily: "DM Mono, monospace",
              fontSize: 9,
              letterSpacing: "0.5px",
              cursor: "pointer",
            }}
            data-testid="sr-add-btn"
          >
            <Plus size={10} /> Add
          </button>
        </RoleGuard>
      </div>

      {srs.length === 0 ? (
        <div
          className="rounded-md p-3 text-center"
          style={{
            background: "#0d0f18",
            border: "1px dashed rgba(255,255,255,0.08)",
            color: "#9198b0",
            fontFamily: "DM Mono, monospace",
            fontSize: 11,
            letterSpacing: "0.3px",
            lineHeight: 1.5,
          }}
          data-testid="sr-section-empty"
        >
          No service requests yet — Add one to coordinate additional work.
        </div>
      ) : (
        <div data-testid="sr-list">
          {srs.map((sr) => (
            <ServiceRequestCard key={sr.id} sr={sr} canEdit={canEdit} />
          ))}
        </div>
      )}

      {showAdd && incident?.id && (
        <ServiceRequestModal
          incidentId={incident.id}
          initialType={presetType}
          onClose={() => { setShowAdd(false); setPresetType(null); }}
        />
      )}
    </div>
  );
};
